import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit } from 'lucide-react';

interface ModeloContrato {
  id: string;
  nome_modelo: string;
  tipo_contrato_id: string;
  conteudo_html: string;
  status: string;
  created_at: string;
  updated_at: string;
  tipo_contrato?: {
    nome: string;
  };
}

export default function ModeloContratoView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission, refetch } = usePermissions();
  const [modelo, setModelo] = useState<ModeloContrato | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'modelos_contrato', 'read');
  const canWrite = hasPermission('recursos_humanos', 'modelos_contrato', 'write');

  console.log('ModeloContratoView - Permissions:', { canRead, canWrite, permissionsChecked, id });

  useEffect(() => {
    // Primeiro refetch das permissões
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    
    if (!canRead || !id) {
      console.log('ModeloContratoView - No read permission or no id, redirecting...');
      navigate('/intranet/recursos-humanos/modelos-contrato');
      return;
    }
    
    fetchModelo();
  }, [id, canRead, permissionsChecked]);

  const fetchModelo = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select(`
          *,
          tipo_contrato!fk_tbl_modelo_contrato_tipo_contrato(nome)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      setModelo(data as any);
    } catch (error) {
      console.error('Erro ao buscar modelo:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar modelo de contrato',
        variant: 'destructive'
      });
      navigate('/intranet/recursos-humanos/modelos-contrato');
    } finally {
      setLoading(false);
    }
  };

  if (!canRead) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Acesso Negado</CardTitle>
          <CardDescription>
            Você não tem permissão para visualizar modelos de contrato.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        Carregando...
      </div>
    );
  }

  if (!modelo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Modelo não encontrado</CardTitle>
          <CardDescription>
            O modelo de contrato solicitado não foi encontrado.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/intranet/recursos-humanos/modelos-contrato')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{modelo.nome_modelo}</h1>
            <p className="text-muted-foreground">
              Visualização do modelo de contrato
            </p>
          </div>
        </div>
        {canWrite && (
          <Button
            onClick={() => navigate(`/intranet/recursos-humanos/modelos-contrato/${modelo.id}/editar`)}
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-1">Nome do Modelo</h4>
                <p className="text-sm text-muted-foreground">{modelo.nome_modelo}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Tipo de Contrato</h4>
                <p className="text-sm text-muted-foreground">
                  {modelo.tipo_contrato?.nome || 'N/A'}
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Status</h4>
                    <Badge variant={modelo.status === 'ativo' ? "default" : "secondary"}>
                      {modelo.status === 'ativo' ? "Ativo" : "Inativo"}
                    </Badge>
              </div>
              <div>
                <h4 className="font-medium mb-1">Criado em</h4>
                <p className="text-sm text-muted-foreground">
                  {new Date(modelo.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Atualizado em</h4>
                <p className="text-sm text-muted-foreground">
                  {new Date(modelo.updated_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>Conteúdo do Contrato</CardTitle>
              <CardDescription>
                Prévia do modelo de contrato com formatação
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="prose prose-sm max-w-none bg-muted/50 p-6 rounded-lg border"
                dangerouslySetInnerHTML={{ __html: modelo.conteudo_html }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}